// ════════════════════════════════════════════════════════════════════════════
// AdminLogsTabV2 — refactor com primitivos Lpa*
//
// vs AdminLogsTab.jsx (legado):
//   • Filtros viram LpaPill (filter chips com aria-pressed)
//   • Tabela vira LpaTable (sortable, sticky header, ARIA aria-sort)
//   • Empty state via LpaEmptyState
//   • Ações destrutivas com confirm dialog (futuro)
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from "react";
import {
  LpaPageHeader,
  LpaTable,
  LpaPill,
  LpaBadge,
  LpaInput,
  LpaField,
  LpaButton,
  type LpaColumn,
} from "../components/ui/lpa";
import { fetchAuditLogs } from "../services/audit.js";
import { logWarn } from "../shared/logger.js";

interface AuditLog {
  id: string;
  action: string;
  user_id?: string;
  client_id?: string;
  details?: Record<string, unknown>;
  created_at: string;
}

interface ThemeLike {
  card: string;
  text: string;
  muted: string;
  border: string;
  bg: string;
}

interface Props {
  T: ThemeLike;
}

const PERIOD_OPTS = [
  { value: "all", label: "Todos" },
  { value: "today", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
] as const;

type Period = (typeof PERIOD_OPTS)[number]["value"];

const ACTION_VARIANT: Record<
  string,
  "info" | "success" | "warning" | "danger" | "accent" | "neutral"
> = {
  snapshot_imported: "info",
  email_report_sent: "success",
  suitability_saved: "accent",
  tax_report_imported: "info",
  login: "neutral",
  client_created: "info",
  cobr_generated: "warning",
  meta_updated: "success",
};

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtRelative(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  return `${Math.floor(diff / 86400)}d atrás`;
}

export default function AdminLogsTabV2({ T: _T }: Props): React.ReactElement {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState<Period>("all");
  const [actionFilter, setActionFilter] = useState<string | null>(null);

  // Fetch logs
  const refresh = () => {
    setLoading(true);
    fetchAuditLogs({ limit: 500 })
      .then((data) => setLogs((data ?? []) as unknown as AuditLog[]))
      .catch((e) => logWarn("AdminLogsTabV2", String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  // Action types únicos
  const actionTypes = useMemo(() => {
    const set = new Set(logs.map((l) => l.action).filter(Boolean));
    return Array.from(set).sort();
  }, [logs]);

  // Filtered
  const filtered = useMemo(() => {
    let out = logs;
    // period
    if (period !== "all") {
      const now = Date.now();
      const cutoff =
        period === "today"
          ? new Date(new Date().toDateString()).getTime()
          : period === "7d"
            ? now - 7 * 86400_000
            : now - 30 * 86400_000;
      out = out.filter((l) => new Date(l.created_at).getTime() >= cutoff);
    }
    // action
    if (actionFilter) {
      out = out.filter((l) => l.action === actionFilter);
    }
    // search
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((l) => {
        const haystack = [
          l.action,
          l.user_id ?? "",
          l.client_id ?? "",
          JSON.stringify(l.details ?? {}),
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return out;
  }, [logs, period, actionFilter, search]);

  // Columns
  const columns: LpaColumn<AuditLog>[] = [
    {
      key: "created_at",
      label: "Quando",
      sortable: true,
      width: "150px",
      render: (r) => (
        <div>
          <div style={{ fontFamily: "var(--lpa-font-mono)", fontSize: "var(--lpa-text-md)" }}>
            {fmtDateTime(r.created_at)}
          </div>
          <div
            style={{
              fontSize: "var(--lpa-text-2xs)",
              color: "var(--lpa-muted)",
              fontFamily: "var(--lpa-font-mono)",
              marginTop: 2,
            }}
          >
            {fmtRelative(r.created_at)}
          </div>
        </div>
      ),
    },
    {
      key: "action",
      label: "Ação",
      sortable: true,
      width: "140px",
      render: (r) => (
        <LpaBadge variant={ACTION_VARIANT[r.action] ?? "neutral"}>{r.action}</LpaBadge>
      ),
    },
    {
      key: "user_id",
      label: "Usuário",
      width: "150px",
      render: (r) => (
        <span
          style={{
            fontFamily: "var(--lpa-font-mono)",
            fontSize: "var(--lpa-text-xs)",
            color: "var(--lpa-muted)",
          }}
        >
          {r.user_id ? r.user_id.slice(0, 8) : "—"}
        </span>
      ),
    },
    {
      key: "client_id",
      label: "Cliente",
      width: "150px",
      render: (r) => (
        <span
          style={{
            fontFamily: "var(--lpa-font-mono)",
            fontSize: "var(--lpa-text-xs)",
            color: "var(--lpa-muted)",
          }}
        >
          {r.client_id ? r.client_id.slice(0, 8) : "—"}
        </span>
      ),
    },
    {
      key: "details",
      label: "Detalhes",
      render: (r) => {
        if (!r.details || Object.keys(r.details).length === 0) {
          return <span style={{ color: "var(--lpa-muted)" }}>—</span>;
        }
        const text = JSON.stringify(r.details);
        return (
          <span
            title={text}
            style={{
              fontFamily: "var(--lpa-font-mono)",
              fontSize: "var(--lpa-text-2xs)",
              color: "var(--lpa-sub)",
              display: "inline-block",
              maxWidth: 280,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 0 }}>
      <LpaPageHeader
        title="Logs de auditoria"
        sub={`${filtered.length} de ${logs.length} eventos exibidos`}
        actions={
          <LpaButton variant="ghost" onClick={refresh}>
            ↻ Atualizar
          </LpaButton>
        }
      />

      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {PERIOD_OPTS.map((p) => (
          <LpaPill
            key={p.value}
            active={period === p.value}
            onClick={() => setPeriod(p.value)}
            variant="accent"
          >
            {p.label}
          </LpaPill>
        ))}
      </div>

      {actionTypes.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          <LpaPill
            active={actionFilter === null}
            onClick={() => setActionFilter(null)}
            variant="neutral"
          >
            Todas as ações
          </LpaPill>
          {actionTypes.map((a) => (
            <LpaPill
              key={a}
              active={actionFilter === a}
              onClick={() => setActionFilter(actionFilter === a ? null : a)}
              variant="neutral"
            >
              {a}
            </LpaPill>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <LpaField label="Busca" labelSrOnly>
          <LpaInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar em action, user_id, client_id, details…"
          />
        </LpaField>
      </div>

      {/* Tabela */}
      <LpaTable
        ariaLabel="Logs de auditoria"
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        pagination={{ pageSize: 50 }}
        loading={loading}
        density="compact"
        stickyHeader
      />
    </div>
  );
}
